import { Node } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";

/**
 * The schema and the HTML contract shared by every rich-text field.
 *
 * Kept apart from the component so the round-trip can be tested without
 * mounting React: what matters about this editor is not how it looks but that
 * opening a stored body and saving it again does not change what the page says.
 *
 * The schema is deliberately narrower than the API sanitizer's allow-list
 * (`my-pet-api` `utils/sanitize/cmsHtml.ts`) and matches what the corpus of
 * migrated WordPress posts actually contains: p, h2, h3, strong, em, u, ul, ol,
 * li, a, figure, img, br, hr, blockquote. Anything else a paste drags in — Word's
 * spans and `mso-` styles, font tags, `<o:p>` — is unwrapped on parse, so the
 * text survives and the markup does not. No class attribute is ever emitted:
 * only Quill's `ql-*` classes survive the sanitizer, and this editor has no use
 * for them.
 */

/**
 * The `figure` wrapper 55 migrated posts use around an image.
 *
 * Two of them wrap the image in a link as well (`figure > a > img`). A link is
 * a *mark* in ProseMirror and marks do not survive on a block image node, so the
 * anchor is modelled as an attribute of the figure and re-rendered as the
 * wrapper it was parsed from. Without this the two posts would silently lose
 * their click-through on the first save.
 */
export const Figure = Node.create({
  name: "figure",
  group: "block",
  // Not `image?`: a handful of migrated figures hold a bare video URL instead
  // of an image, and a stricter content expression would delete the text on
  // open — the editor must not lose what it cannot render specially.
  content: "block*",

  addAttributes() {
    return {
      href: {
        default: null,
        parseHTML: (element) =>
          element.querySelector(":scope > a[href]")?.getAttribute("href") || null,
        // Rendered as the wrapping anchor below, never as an attribute of the
        // figure itself.
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML: () => [{ tag: "figure" }],

  renderHTML: ({ node }) =>
    node.attrs.href ? ["figure", ["a", { href: node.attrs.href }, 0]] : ["figure", 0],
});

export const buildExtensions = () => [
  StarterKit.configure({
    heading: { levels: [2, 3] },
    // Reachable without a toolbar button — backtick/tilde input rules and
    // pastes from Slack/GitHub — but `code`, `pre` and `s` are not in the API
    // allow-list, so the site would silently drop what the editor showed.
    code: false,
    codeBlock: false,
    strike: false,
    // Nothing is injected on a link: `target` and `rel` that the stored body
    // already carries are parsed and re-rendered, but a link typed here gets
    // neither, so saving an untouched post does not rewrite its markup.
    link: {
      openOnClick: false,
      HTMLAttributes: { target: null, rel: null, class: null },
    },
  }),
  Image,
  Figure,
];

/** TipTap's empty document — a single empty paragraph, not an empty string. */
const EMPTY_DOCUMENTS = [
  "",
  "<p></p>",
  "<p><br></p>",
  '<p><br class="ProseMirror-trailingBreak"></p>',
];

/**
 * `editor.getHTML()` → the shape the API stores.
 *
 * Three things differ between what ProseMirror serializes and what the corpus
 * holds, and all three are cosmetic rather than semantic:
 *
 * - a list item is a block container, so ProseMirror wraps its text in a
 *   paragraph; the corpus does not, and the site's CSS does not expect it;
 * - deleting the contents of a list or a figure can leave the empty shell
 *   behind, which renders as a stray bullet or a gap;
 * - the editor keeps a spare paragraph after a trailing heading, list or image
 *   so there is somewhere to click; it is scaffolding, not content;
 * - an empty document is a paragraph, and storing that would count as a body.
 */
export const normalizeEditorHtml = (html) => {
  if (!html) return "";

  const doc = new DOMParser().parseFromString(html, "text/html");

  doc.querySelectorAll("li").forEach((li) => {
    if (li.children.length === 1 && li.children[0].tagName === "P") {
      li.replaceChildren(...li.children[0].childNodes);
    }
  });

  doc.querySelectorAll("li").forEach((li) => {
    if (!li.textContent.trim() && !li.querySelector("img")) li.remove();
  });

  doc.querySelectorAll("ul, ol, figure").forEach((element) => {
    if (element.children.length === 0) element.remove();
  });

  while (
    doc.body.lastElementChild?.tagName === "P" &&
    doc.body.lastElementChild.childNodes.length === 0
  ) {
    doc.body.lastElementChild.remove();
  }

  // Belt and braces: the sanitizer keeps only `ql-*` classes, so a class that
  // rode in on a paste would be dropped server-side anyway — dropping it here
  // means the editor and the site agree on what was saved.
  doc.querySelectorAll("[class]").forEach((element) => element.removeAttribute("class"));

  const out = doc.body.innerHTML.trim();
  return EMPTY_DOCUMENTS.includes(out) ? "" : out;
};
